import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Avatar, Stack, Chip, Card } from '@mui/material';
import { Icon } from '@iconify/react';
import { get } from 'lodash';
import Page from '../../components/Page';
import config from '../../util/config';
import { getAllTeachersPublic } from '../../axiosLib/services/teacher/user';

export default function CoolTeachersPage() {
  const [teachers, setTeachers] = useState([]);

  useEffect(() => {
    getAllTeachersPublic((error, data) => {
      if (!error) setTeachers(get(data, 'teachers', []));
    });
  }, []);

  return (
    <Page title="শিক্ষক মণ্ডলী — Our Teachers">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        .cool-teachers * { font-family: 'Inter', sans-serif; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(30px); } to { opacity:1; transform:translateY(0); } }
        .fade-up { animation: fadeUp 0.6s ease-out both; }
      `}</style>

      <Box className="cool-teachers">
        {/* ═══════ HERO ═══════ */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
          py: { xs: 6, md: 8 }, position: 'relative', overflow: 'hidden', textAlign: 'center',
        }}>
          <Box sx={{
            position: 'absolute', top: -80, right: -80, width: 300, height: 300,
            borderRadius: '50%', background: 'rgba(99,102,241,0.15)', filter: 'blur(60px)',
          }} />
          <Box sx={{
            position: 'absolute', bottom: -60, left: -60, width: 200, height: 200,
            borderRadius: '50%', background: 'rgba(0,212,255,0.1)', filter: 'blur(50px)',
          }} />

          <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{
              width: 64, height: 64, borderRadius: 3, display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', mb: 3,
              background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)',
            }}>
              <Icon icon="mdi:account-group" style={{ fontSize: 32, color: '#a5b4fc' }} />
            </Box>
            <Typography sx={{
              fontSize: { xs: '1.8rem', md: '2.5rem' }, fontWeight: 900, color: '#fff', mb: 1.5,
            }}>
              আমাদের শিক্ষক মণ্ডলী
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '1rem', maxWidth: 500, mx: 'auto' }}>
              দেশের সেরা শিক্ষকদের তত্ত্বাবধানে আপনার মেডিকেল ভর্তি প্রস্তুতি
            </Typography>
          </Container>
        </Box>

        {/* ═══════ TEACHER CARDS ═══════ */}
        <Box sx={{ py: 6, background: '#f5f7fb', minHeight: '50vh' }}>
          <Container maxWidth="lg">
            {teachers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Icon icon="mdi:account-group-outline" style={{ fontSize: 72, color: '#c7d2fe' }} />
                <Typography sx={{ color: '#888', mt: 2, fontSize: '1.1rem' }}>
                  শিক্ষক তথ্য শীঘ্রই আপডেট করা হবে
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                gap: 3,
              }}>
                {teachers.map((teacher, i) => (
                  <Card key={i} className="fade-up" sx={{
                    borderRadius: 4, overflow: 'hidden', transition: 'all 0.3s',
                    border: '1px solid rgba(99,102,241,0.06)',
                    '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 16px 40px rgba(99,102,241,0.15)' },
                  }}
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    {/* Gradient header with avatar */}
                    <Box sx={{
                      height: 180, position: 'relative',
                      background: `linear-gradient(135deg, #1e1b4b, #4338ca)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {/* Decorative circles */}
                      <Box sx={{
                        position: 'absolute', top: -20, right: -20, width: 80, height: 80,
                        borderRadius: '50%', background: 'rgba(255,255,255,0.05)',
                      }} />
                      <Box sx={{
                        position: 'absolute', bottom: -15, left: -15, width: 60, height: 60,
                        borderRadius: '50%', background: 'rgba(255,255,255,0.04)',
                      }} />
                      <Avatar
                        src={teacher.image ? config.BASE_API_URL + teacher.image : ''}
                        sx={{
                          width: 110, height: 110,
                          border: '4px solid rgba(255,255,255,0.2)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                        }}
                      >
                        <Icon icon="mdi:account" style={{ fontSize: 48 }} />
                      </Avatar>
                    </Box>

                    {/* Content */}
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography sx={{ fontWeight: 800, color: '#1e1b4b', fontSize: '1.15rem', mb: 0.5 }}>
                        {teacher.name}
                      </Typography>

                      {teacher.designation && (
                        <Typography sx={{ color: '#6366f1', fontWeight: 600, fontSize: '0.85rem', mb: 1 }}>
                          {teacher.designation}
                        </Typography>
                      )}

                      {teacher.institution && (
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center"
                          sx={{ mb: 1.5 }}>
                          <Icon icon="mdi:school" style={{ fontSize: 16, color: '#888' }} />
                          <Typography sx={{ color: '#888', fontSize: '0.8rem' }}>
                            {teacher.institution}
                          </Typography>
                        </Stack>
                      )}

                      {teacher.subjects && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center" useFlexGap>
                          {teacher.subjects.split(',').map((s, si) => (
                            <Chip key={si} label={s.trim()} size="small"
                              sx={{
                                background: 'rgba(99,102,241,0.08)', color: '#4338ca',
                                fontWeight: 600, fontSize: '0.72rem',
                              }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Card>
                ))}
              </Box>
            )}
          </Container>
        </Box>
      </Box>
    </Page>
  );
}
